import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import moment from "moment";
import {
  del_weight_list_api,
  get_weight_list_api,
} from "src/DAL/exercise/exercise";
import { EditProfileApiDashBoard } from "src/DAL/Profile/Profile";
import { _get_user_from_localStorage_new } from "src/DAL/localStorage/localStorage";
import WeightWaistChart from "src/components/WeightWaistChart";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrStatTile from "src/design-system/components/GrStatTile";
import GrNotice from "src/design-system/components/GrNotice";
import GrFilterChips from "src/design-system/components/GrFilterChips";
import GrFilterDrawer from "src/design-system/components/GrFilterDrawer";
import GrFilterButton from "src/design-system/components/GrFilterButton";
import GrActiveFilters from "src/design-system/components/GrActiveFilters";
import GrDataTable from "src/design-system/components/GrDataTable";
import GrDialog from "src/design-system/components/GrDialog";
import GrConfirmDialog from "src/design-system/components/GrConfirmDialog";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrText from "src/design-system/components/GrText";
import { GrMenu, GrMenuItem } from "src/design-system/components/GrMenu";
import AddUnits from "./AddUnits";
import AddWeight from "./AddWeight";
import WeightFilter from "./WeightFilter";

/**
 * Weight Tracker (/weight-tracker): where the member stands (current, target, to go, body fat),
 * an on-track note, the progress chart by period, and every check-in (edit / delete, date
 * filter, load more). Check in and Units & target open dialogs.
 */

const PAGE_SIZE = 50;
const EMPTY_FILTER = {
  date_from: null,
  date_to: null,
};
// Same key as before, so a saved date filter still applies.
const FILTER_KEY = "UserFormSListSearch";

const PERIODS = [
  { value: "7_day", label: "7 days" },
  { value: "30_day", label: "30 days" },
  { value: "90_day", label: "90 days" },
  { value: "1_year", label: "1 year" },
  { value: "all", label: "All" },
];

const MEASUREMENTS = [
  { key: "chest", label: "Chest" },
  { key: "waist", label: "Waist" },
  { key: "hip", label: "Hip" },
  { key: "arm", label: "Arm" },
  { key: "thigh", label: "Thigh" },
];

const round1 = (value) => Math.round(Number(value) * 10) / 10;
const hasValue = (value) => value !== null && value !== undefined && value !== "" && Number(value) !== 0;

const tilesSx = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
  gap: gr.space[3],
};
const headerActionsSx = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: gr.space[2] };
const cellStackSx = { display: "grid", gap: "2px", minWidth: 0 };
const noteSx = {
  color: gr.color.muted,
  fontSize: gr.font.size.xs,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
};

export default function WeightTracker() {
  const { enqueueSnackbar } = useSnackbar();
  const [timePeriod, setTimePeriod] = useState("7_day");
  const [loadState, setLoadState] = useState("loading");
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openWeight, setOpenWeight] = useState(false);
  const [openSetting, setOpenSetting] = useState(false);
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [userPre, setUserPre] = useState({});
  const [chartList, setChartList] = useState([]);
  const [editValues, setEditValues] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [dataList, setDataList] = useState(null);
  const [showAction, setShowAction] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [deficitAllow, setDeficitAllow] = useState(true);
  const [filterDrawerState, setFilterDrawerState] = useState(false);
  const [filterData, setFilterData] = useState(EMPTY_FILTER);
  // The date range the list was loaded with (the drawer may hold picks not applied yet).
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_FILTER);
  const [menu, setMenu] = useState(null); // { anchor, row }

  let userInfo = _get_user_from_localStorage_new();

  // page 0 replaces the list, later pages are added under it (Load more).
  const weight_list = async (value = appliedFilter, nextPage = 0) => {
    let start_data = "";
    let end_date = "";

    if (value?.date_from || value?.date_to) {
      if (!value?.date_from || !value?.date_to) {
        return enqueueSnackbar("Please enter both start and end date", {
          variant: "error",
        });
      }
    }
    if (value?.date_from) {
      start_data = moment(value?.date_from).format("YYYY-MM-DD");
    }
    if (value?.date_to) {
      end_date = moment(value?.date_to).format("YYYY-MM-DD");
    }

    if (nextPage > 0) setLoadingMore(true);
    else setRefreshing(true);
    handleCloseFilterDrawer();
    const result = await get_weight_list_api(
      nextPage,
      PAGE_SIZE,
      timePeriod,
      start_data,
      end_date,
    );
    if (result.code === 200) {
      const rows = result.weights || [];
      setUserList((prev) => (nextPage > 0 ? [...prev, ...rows] : rows));
      setPage(nextPage);
      setAppliedFilter(value || EMPTY_FILTER);
      setChartList(result.graph_weights);
      setDataList(result);
      setUserPre(result.user_preferences);
      setTotalCount(result.totalEntries);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setLoadState("ready");
    setRefreshing(false);
    setLoadingMore(false);
  };
  const reload = () => weight_list(appliedFilter, 0);

  const handleSelectOther = (name, value) => {
    setFilterData((values) => ({ ...values, [name]: value }));
  };

  const handleOpenFilterDrawer = () => {
    setFilterDrawerState(true);
  };

  const handleCloseFilterDrawer = () => {
    setFilterDrawerState(false);
  };

  const searchFunction = () => {
    weight_list(filterData, 0);
    localStorage.setItem(FILTER_KEY, JSON.stringify(filterData));
  };

  const handleClearFilter = () => {
    setFilterData(EMPTY_FILTER);
    weight_list(EMPTY_FILTER, 0);
    localStorage.removeItem(FILTER_KEY);
  };

  const openCheckIn = () => {
    setEditValues(null);
    setOpenWeight(true);
  };

  const handleEdit = (value) => {
    setEditValues(value);
    setOpenWeight(true);
  };

  const handleDelete = async () => {
    const row = deleteRow;
    setDeleteRow(null);
    if (!row?._id) return;
    const result = await del_weight_list_api(row._id);
    if (result.code === 200) {
      reload();
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const runMenu = (action) => {
    const row = menu?.row;
    setMenu(null);
    if (row) action(row);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    setIsFormLoading(true);
    const result = await EditProfileApiDashBoard(formData, "weight");
    if (result.code === 200) {
      localStorage.setItem(`user_data`, JSON.stringify(result?.member));
      localStorage.setItem(`user_data_new`, JSON.stringify(result?.member));
      enqueueSnackbar("Pace calculator updated successfully", {
        variant: "success",
      });
      setShowAction(false);
      setIsFormLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsFormLoading(false);
    }
  };

  let check_unit = localStorage.getItem("unit_added");
  // The API sends lbs values for every unit other than kg (also before units are saved).
  const weightUnit = !dataList || userPre?.weight_unit === "kg" ? "kg" : "lbs";
  const current_weight =
    userPre?.weight_unit == "kg"
      ? dataList?.current_weight_in_kg
      : dataList?.current_weight_in_lbs;
  const target = dataList?.target_weight;

  useEffect(() => {
    if (!check_unit) {
      setOpenSetting(true);
    }
  }, [check_unit]);

  useEffect(() => {
    let filter_data = EMPTY_FILTER;
    let find_filter = localStorage.getItem(FILTER_KEY);
    if (find_filter) {
      filter_data = JSON.parse(find_filter);
    }
    setFilterData(filter_data);
    weight_list(filter_data, 0);
  }, [timePeriod]);

  useEffect(() => {
    if (
      current_weight &&
      dataList?.target_weight &&
      dataList?.predictedDate &&
      userInfo?.deficit_percentage
    ) {
      if (current_weight == dataList?.target_weight) {
        setShowAction(true);
      }
    }
  }, [dataList, current_weight, userInfo]);

  if (loadState === "loading") {
    return <GrLoader label="Loading your weight tracker" sx={{ py: gr.space[12] }} />;
  }

  const latest = userList[0] || null;
  const toGo = hasValue(current_weight) && hasValue(target) ? round1(Math.abs(current_weight - target)) : null;
  const filtered = Boolean(appliedFilter?.date_from && appliedFilter?.date_to);
  // The applied date range as one chip; × = the existing Reset (all dates).
  const activeFilters = filtered
    ? [
        {
          key: "range",
          icon: icons.calendar,
          label: `${moment(appliedFilter.date_from).format("D MMM YYYY")} – ${moment(appliedFilter.date_to).format("D MMM YYYY")}`,
          onClear: handleClearFilter,
        },
      ]
    : [];
  const unitOf = (key) => userPre?.[`${key}_unit`] || "";
  const measurementText = (row) =>
    MEASUREMENTS.filter((m) => hasValue(row?.[m.key]))
      .map((m) => `${m.label} ${round1(row[m.key])} ${unitOf(m.key)}`)
      .join(" · ");

  return (
    <GrPage>
      <GrPageHeader
        title="Weight Tracker"
        subtitle="Log your weight and see how you're moving towards your target."
        actions={
          <Box sx={headerActionsSx}>
            <GrButton variant="secondary" onClick={() => setOpenSetting(true)}>
              Units & target
            </GrButton>
            <GrButton startIcon={<GrIcon icon={icons.plus} />} onClick={openCheckIn}>
              Check in
            </GrButton>
          </Box>
        }
      />

      <Box sx={{ display: "grid", gap: gr.space[6] }}>
        <Box sx={tilesSx}>
          <GrStatTile
            variant="card"
            icon={<GrIcon icon={icons.weight} size="md" />}
            value={hasValue(current_weight) ? `${round1(current_weight)} ${weightUnit}` : "—"}
            label="Current weight"
            hint={latest?.date ? `On ${moment(latest.date).format("D MMM YYYY")}` : "No check-in yet"}
          />
          <GrStatTile
            variant="card"
            tone="ok"
            icon={<GrIcon icon={icons.flag} size="md" />}
            value={hasValue(target) ? `${round1(target)} ${weightUnit}` : "Not set"}
            label="Target weight"
            hint={
              <GrButton variant="link" size="sm" onClick={() => setOpenSetting(true)}>
                {hasValue(target) ? "Edit target" : "Set a target"}
              </GrButton>
            }
          />
          <GrStatTile
            variant="card"
            tone="late"
            icon={<GrIcon icon={icons.trendDown} size="md" />}
            value={toGo === null ? "—" : toGo === 0 ? "Reached" : `${toGo} ${weightUnit}`}
            label="To go"
            hint={
              toGo && dataList?.predictedDate
                ? `Reach by ${moment(dataList.predictedDate).format("D MMM YYYY")}`
                : undefined
            }
          />
          <GrStatTile
            variant="card"
            icon={<GrIcon icon={icons.droplet} size="md" />}
            value={hasValue(latest?.body_fat) ? `${round1(latest.body_fat)}%` : "—"}
            label="Body fat"
            hint={hasValue(latest?.lean_mass) ? `Lean mass ${round1(latest.lean_mass)} ${weightUnit}` : undefined}
          />
        </Box>

        {toGo && dataList?.predictedDate ? (
          <GrNotice
            icon={<GrIcon icon={icons.trendDown} />}
            title={`On track for ${round1(target)} ${weightUnit} by ${moment(dataList.predictedDate).format("D MMM YYYY")}`}
            text={
              dataList?.weightLoss
                ? `You've lost ${round1(dataList.weightLoss)} ${weightUnit} in the last 2 weeks. Keep going!`
                : "Based on your progress so far. Keep checking in to stay on track."
            }
          />
        ) : null}

        <GrCard>
          <GrCardHeader
            as="h2"
            title="Progress"
            subtitle="Pick a line above the chart: weight, body fat or a measurement."
            action={
              <GrFilterChips
                ariaLabel="Period"
                value={timePeriod}
                onChange={(value) => setTimePeriod(value)}
                options={PERIODS}
              />
            }
            wrapAction
          />
          {chartList && chartList.length ? (
            <WeightWaistChart height={320} chartList={chartList} userPre={userPre} compact />
          ) : (
            <GrEmptyState
              compact
              icon={<GrIcon icon={icons.activity} />}
              title="Nothing to chart in this period"
              text="Pick a longer period or check in to add a point."
            />
          )}
        </GrCard>

        <GrCard>
          <GrCardHeader
            as="h2"
            title="Check-ins"
            subtitle={`${totalCount} check-in${totalCount === 1 ? "" : "s"}`}
            action={
              <GrFilterButton
                size="sm"
                icon={icons.calendar}
                count={activeFilters.length}
                onClick={handleOpenFilterDrawer}
              >
                Filter by date
              </GrFilterButton>
            }
            wrapAction
          />
          <GrActiveFilters
            filters={activeFilters}
            onOpen={handleOpenFilterDrawer}
            sx={{ mb: gr.space[4] }}
          />
          {userList.length === 0 ? (
            <GrEmptyState
              compact
              icon={<GrIcon icon={icons.activity} />}
              title={filtered ? "No check-ins between these dates" : "No check-ins yet"}
              text={filtered ? "Try other dates." : "Log your first weight to start your chart."}
              action={
                filtered ? (
                  <GrButton variant="secondary" size="sm" onClick={handleClearFilter}>
                    Reset dates
                  </GrButton>
                ) : (
                  <GrButton size="sm" onClick={openCheckIn}>
                    Check in
                  </GrButton>
                )
              }
            />
          ) : (
            <Box aria-busy={refreshing || undefined} sx={{ opacity: refreshing ? 0.6 : 1, transition: "opacity .15s ease" }}>
              <GrDataTable
                ariaLabel="Weight check-ins"
                rows={userList}
                getRowKey={(row) => row._id}
                columns={[
                  {
                    key: "date",
                    label: "Date",
                    primary: true,
                    width: "minmax(0, 1.1fr)",
                    render: (row) => (
                      <Box sx={cellStackSx}>
                        <GrText variant="body">{moment(row.date).format("D MMM YYYY")}</GrText>
                        {row.time ? (
                          <GrText variant="caption">{moment(row.time, "HH:mm").format("h:mm A")}</GrText>
                        ) : null}
                      </Box>
                    ),
                  },
                  {
                    key: "weight",
                    label: `Weight (${weightUnit})`,
                    width: "minmax(0, 0.8fr)",
                    render: (row) => (hasValue(row.current_weight) ? round1(row.current_weight) : "—"),
                  },
                  {
                    key: "fat",
                    label: "Body fat",
                    width: "minmax(0, 0.7fr)",
                    render: (row) => (hasValue(row.body_fat) ? `${round1(row.body_fat)}%` : "—"),
                  },
                  {
                    key: "lean",
                    label: `Lean mass (${weightUnit})`,
                    width: "minmax(0, 0.9fr)",
                    render: (row) => (hasValue(row.lean_mass) ? round1(row.lean_mass) : "—"),
                  },
                  {
                    key: "more",
                    label: "Measurements & note",
                    width: "minmax(0, 2fr)",
                    render: (row) => {
                      const text = measurementText(row);
                      return (
                        <Box sx={cellStackSx}>
                          <GrText variant="secondary">{text || "—"}</GrText>
                          {row.note ? (
                            <Box component="span" title={row.note} sx={noteSx}>
                              {row.note}
                            </Box>
                          ) : null}
                        </Box>
                      );
                    },
                  },
                  {
                    key: "actions",
                    label: "Actions",
                    action: true,
                    render: (row) => (
                      <GrIconButton
                        size="sm"
                        aria-label={`Actions for the check-in on ${moment(row.date).format("D MMM YYYY")}`}
                        aria-haspopup="menu"
                        onClick={(e) => setMenu({ anchor: e.currentTarget, row })}
                      >
                        <GrIcon icon={icons.more} />
                      </GrIconButton>
                    ),
                  },
                ]}
              />
              {userList.length < totalCount ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: gr.space[4] }}>
                  <GrButton
                    variant="secondary"
                    loading={loadingMore}
                    onClick={() => weight_list(appliedFilter, page + 1)}
                  >
                    Load more
                  </GrButton>
                </Box>
              ) : null}
            </Box>
          )}
        </GrCard>
      </Box>

      <GrMenu anchorEl={menu?.anchor || null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <GrMenuItem onClick={() => runMenu(handleEdit)}>
          <GrIcon icon={icons.edit} />
          Edit
        </GrMenuItem>
        <GrMenuItem danger onClick={() => runMenu((row) => setDeleteRow(row))}>
          <GrIcon icon={icons.trash} />
          Delete
        </GrMenuItem>
      </GrMenu>

      <GrConfirmDialog
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        onConfirm={handleDelete}
        destructive
        title="Delete this check-in?"
        text={
          deleteRow?.date
            ? `The check-in from ${moment(deleteRow.date).format("D MMM YYYY")} will be removed from your chart.`
            : undefined
        }
        confirmLabel="Delete"
      />

      <GrDialog
        open={openWeight}
        onClose={() => setOpenWeight(false)}
        title={editValues ? "Edit check-in" : "Check in"}
        maxWidth={640}
      >
        <AddWeight
          openWeight={openWeight}
          setOpenWeight={setOpenWeight}
          weight_list={reload}
          editValues={editValues}
          userPre={userPre}
        />
      </GrDialog>

      <GrDialog
        open={openSetting}
        // The first time, units have to be saved before the page can be used.
        onClose={check_unit ? () => setOpenSetting(false) : undefined}
        title="Units & target"
        maxWidth={560}
      >
        <AddUnits
          openSetting={openSetting}
          setOpenSetting={setOpenSetting}
          weight_list={reload}
          canCancel={Boolean(check_unit)}
        />
      </GrDialog>

      {userInfo?.deficit_percentage && (
        <GrDialog
          open={showAction}
          title="Target weight reached"
          maxWidth={440}
          actions={
            <GrButton
              disabled={deficitAllow}
              loading={isFormLoading}
              onClick={(e) => {
                handleUpdate(e);
              }}
            >
              Save
            </GrButton>
          }
        >
          <Box component="p" sx={{ m: 0, mb: gr.space[3] }}>
            Congratulations! You have reached your target weight. Please turn off the calorie deficit.
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={deficitAllow}
                onChange={(e) => {
                  setDeficitAllow(e.target.checked);
                }}
              />
            }
            label="Calorie deficit"
          />
        </GrDialog>
      )}

      <GrFilterDrawer
        open={filterDrawerState}
        onClose={handleCloseFilterDrawer}
        title="Filter by date"
        onClear={handleClearFilter}
        clearLabel="Reset"
        onApply={searchFunction}
        applyLabel="Apply"
      >
        <WeightFilter filterData={filterData} handleSelectOther={handleSelectOther} />
      </GrFilterDrawer>
    </GrPage>
  );
}
